import React, { useState, useEffect, useRef } from 'react';
import styles from './PriestPackageComponent.module.css';
import { createPriestEventspackage, updatePriestEventspackage } from '../../Apiservices/Auth/servers/fetchProviderData';
import { CustomDatePicker } from './CustomDatePicker';
import { format } from 'date-fns';

const CreateEditPriestPackageModal = ({ 
  mode = 'create', // 'create' or 'edit'
  packageData = null, // For edit mode
  onSave,
  onCancel,
  recommendedEvents = [], // Array of events with id and name
}) => {
  const [formData, setFormData] = useState({
    packageName: '',
    description: '',
    price: '',
    offeredPrice: '',
    validTill: '',
    recommendedEvents: [], // Will store IDs as comma-separated string
    recommendedEventNames: [], // Will store names for display
    numberOfPriests: '1',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showEventsDropdown, setShowEventsDropdown] = useState(false);
  const [eventsSearch, setEventsSearch] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const eventsDropdownRef = useRef(null);
  const calendarRef = useRef(null);
  
  // Filter events based on search
  const filteredEvents = recommendedEvents.filter(event =>
    event.name.toLowerCase().includes(eventsSearch.toLowerCase())
  ).slice(0, 6); // Show max 6 events

  // Initialize form with data in edit mode
  useEffect(() => {
  if (mode === 'edit' && packageData) {
      console.log("Edit mode packageData:", packageData); 
    let eventIds = [];
    let eventNames = [];
    
     // Check if packageData.recommendedEvents exists and is not empty
    if (packageData.recommendedEvents && Array.isArray(packageData.recommendedEvents)) {
      // Extract event IDs and names from the objects
      eventIds = packageData.recommendedEvents.map(event => event.eventId);
      eventNames = packageData.recommendedEvents.map(event => event.eventName);
    }
    
    console.log("Extracted eventIds:", eventIds);
    console.log("Extracted eventNames:", eventNames);
     
    setFormData({
      packageName: packageData.packageName || '', // Change from 'name' to 'packageName'
      description: packageData.description || '',
      price: packageData.price || '',
      offeredPrice: packageData.offeredPrice || '',
      validTill: packageData.validTill || '',
      recommendedEvents: eventIds, // Array of event IDs
      recommendedEventNames: eventNames, // Array of event names
      numberOfPriests: packageData.numberOfPriests || '0',
    });
  }
}, [mode, packageData, recommendedEvents]);

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };

    if (showCalendar) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCalendar]);

const handleInputChange = (name, value) => {
  setFormData(prev => ({
    ...prev,
    [name]: value
  }));
  setError('');
  
  // If offer date is cleared, also clear offer price
  if (name === 'validTill' && !value && formData.offeredPrice) {
    setFormData(prev => ({
      ...prev,
      offeredPrice: ''
    }));
    setShowCalendar(false);
  }
  
  // Clear validTill if offeredPrice is cleared
  if (name === 'offeredPrice' && !value) {
    setFormData(prev => ({
      ...prev,
      validTill: ''
    }));
    setShowCalendar(false);
  }
  
  // Show calendar when offered price is entered and we're in create mode or editing empty date
  if (name === 'offeredPrice' && value && (!formData.validTill || mode === 'create')) {
    setShowCalendar(true);
  }
};

 const handleEventSelect = (event) => {
  const currentIds = [...formData.recommendedEvents];
  const currentNames = [...formData.recommendedEventNames];
  
  // Check if event is already selected
  const isSelected = formData.recommendedEvents.includes(event.id);

  
  if (isSelected) {
    // Remove event
    const index = currentIds.indexOf(event.id);
    currentIds.splice(index, 1);
    currentNames.splice(index, 1);
  } else {
    // Add event (max 4)
    if (currentIds.length >= 4) {
      alert('You can select maximum 4 events');
      return;
    }
    currentIds.push(event.id);
    currentNames.push(event.name);
  }
  
  setFormData(prev => ({
    ...prev,
    recommendedEvents: currentIds,
    recommendedEventNames: currentNames
  }));
  
  setEventsSearch('');
  setShowEventsDropdown(false);
};

  const removeSelectedEvent = (eventId, eventName) => {
  const currentIds = [...formData.recommendedEvents];
  const currentNames = [...formData.recommendedEventNames];
  
  const index = currentIds.indexOf(parseInt(eventId));
  if (index > -1) {
    currentIds.splice(index, 1);
    currentNames.splice(index, 1);
  }
  
  setFormData(prev => ({
    ...prev,
    recommendedEvents: currentIds,
    recommendedEventNames: currentNames
  }));
};
// Add this function after the other functions
const formatDateForDisplay = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return format(date, 'dd MMM yyyy'); // e.g., "15 Dec 2024"
  } catch (error) {
    return '';
  }
};
  const handleDateChange = (dateString) => {
  // If date is cleared, also clear offer price
  if (!dateString && formData.offeredPrice) {
    setFormData(prev => ({
      ...prev,
      validTill: '',
      offeredPrice: ''
    }));
  } else {
    setFormData(prev => ({
      ...prev,
      validTill: dateString
    }));
  }
};

 const handleSubmit = async (e) => {
  e.preventDefault();
  
  if (!formData.packageName.trim()) {
    setError('Package name is required');
    return;
  }

  if (!formData.price || isNaN(formData.price)) {
    setError('Valid price is required');
    return;
  }

  // If offered price is provided, validation is required
  if (formData.offeredPrice) {
    if (isNaN(formData.offeredPrice)) {
      setError('Valid offered price is required');
      return;
    }

    if (parseFloat(formData.offeredPrice) > parseFloat(formData.price)) {
      setError('Offered price cannot be greater than original price');
      return;
    }

    if (!formData.validTill) {
      setError('Valid till date is required when offering a discount');
      return;
    }
  }

  setLoading(true);
  try {
    const requestData = {
       packageId: mode === 'edit' ? packageData.packageId : undefined, 
      packageName: formData.packageName.trim(),
      description: formData.description.trim(),
      price: parseFloat(formData.price),
      offeredPrice: formData.offeredPrice ? parseFloat(formData.offeredPrice) : parseFloat(formData.price),
      validTill: formData.offeredPrice ? formData.validTill : null,
      recommendedEvents: formData.recommendedEvents,
      numberOfPriests: parseInt(formData.numberOfPriests) || 1,
      isActive: true,
    };

    let response;
    
    if (mode === 'create') {
      response = await createPriestEventspackage(requestData);
    } else {
      
      
      response = await updatePriestEventspackage(requestData);
    }
    
    // Log the response to see its structure
    console.log('API Response:', response);
    
    // Check response structure - response should have data.success
    if (response && response.data && response.data.success === true) {
      // Show success message
      alert(`Package ${mode === 'create' ? 'created' : 'updated'} successfully!`);
      
      if (onSave) {
        // Pass the complete response or create a package object
        const savedPackage = {
          id: response.data.packageId || packageData?.id,
          name: formData.packageName,
          description: formData.description,
          price: parseFloat(formData.price),
          offeredPrice: formData.offeredPrice ? parseFloat(formData.offeredPrice) : parseFloat(formData.price),
          validTill: formData.validTill,
          recommendedEvents: formData.recommendedEvents,
          numberOfPriests: parseInt(formData.numberOfPriests) || 1,
          isActive: true
        };
        onSave(savedPackage);
      }
      
      // Reset form for create mode
      if (mode === 'create') {
        setFormData({
          packageName: '',
          description: '',
          price: '',
          offeredPrice: '',
          validTill: '',
          recommendedEvents: [],
          recommendedEventNames: [],
          numberOfPriests: '1',
        });
      }
      setError('');
    } else {
      // Try to get error message from different possible response structures
      const errorMessage = 
        response?.data?.message || 
        response?.message || 
        response?.data?.error || 
        `Failed to ${mode} package`;
      
      throw new Error(errorMessage);
    }
    
  } catch (err) {
    setError(err.message || 'An error occurred. Please try again.');
  } finally {
    setLoading(false);
  }
};

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (eventsDropdownRef.current && !eventsDropdownRef.current.contains(event.target)) {
        setShowEventsDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get the display value for the input
  const getEventsDisplayValue = () => {
    if (showEventsDropdown) {
      return eventsSearch;
    }
    return  '';
  };

  const handleEventsInputClick = () => {
    setShowEventsDropdown(true);
    setEventsSearch('');
  };

  const handleEventsInputChange = (e) => {
    setEventsSearch(e.target.value);
    setShowEventsDropdown(true);
  };

  // Get tomorrow's date for min date
  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString();
  };

  return (
    <div className={styles.createEditModal}>
      <div className={styles.modalHeader}>
        <h2>{mode === 'create' ? 'Create New Package' : 'Edit Package'}</h2>
        <button 
          className={styles.closeButton}
          onClick={handleCancel}
          disabled={loading}
        >
          ×
        </button>
      </div>
      
      <form onSubmit={handleSubmit} className={styles.packageForm}>
        <div className={styles.formGroup}>
          <label htmlFor="packageName">Package Name *</label>
          <input
            type="text"
            id="packageName"
            name="packageName"
            value={formData.packageName}
            onChange={(e) => handleInputChange('packageName', e.target.value)}
            placeholder="Enter package name (e.g., Wedding Package, Festival Package)"
            disabled={loading}
            required
          />
        </div>
        
        <div className={styles.formGroup}>
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Describe what's included in this package..."
            rows="4"
            disabled={loading}
          />
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="price">Original Price (₹) *</label>
            <input
              type="number"
              id="price"
              name="price"
              value={formData.price}
              onChange={(e) => handleInputChange('price', e.target.value)}
              placeholder="Enter original price"
              disabled={loading}
              min="0"
              step="0.01"
              required
            />
          </div>
          
          <div className={styles.formGroup}>
            <div className={styles.offerPriceContainer}>
              <label htmlFor="offeredPrice">Offered Price (₹) {formData.offeredPrice ? '*' : ''}</label>
              <div className={styles.offerInputWrapper}>
                <input
                  type="number"
                  id="offeredPrice"
                  name="offeredPrice"
                  value={formData.offeredPrice}
                  onChange={(e) => handleInputChange('offeredPrice', e.target.value)}
                  placeholder="Enter offered price"
                  disabled={loading}
                  min="0"
                  step="0.01"
                  className={styles.offeredPriceInput}
                />
                {/* {formData.offeredPrice && formData.price && (
                  <div className={styles.discountInfo}>
                    <span className={styles.discountPercent}>
                      {((1 - (parseFloat(formData.offeredPrice) / parseFloat(formData.price))) * 100).toFixed(1)}% OFF
                    </span>
                    <span className={styles.saveAmount}>
                      Save ₹{(parseFloat(formData.price) - parseFloat(formData.offeredPrice)).toFixed(2)}
                    </span>
                  </div>
                )} */}
              </div>
              
              {/* Show selected date under offer price */}
{formData.offeredPrice && formData.validTill && (
  <div className={styles.selectedDateDisplay}>
    <div className={styles.dateLabel}>
      <i className="pi pi-calendar" style={{ marginRight: '8px' }}></i>
      Offer valid until: 
    </div>
    <div className={styles.dateValue}>
      {formatDateForDisplay(formData.validTill)}
      <button 
        type="button"
        className={styles.clearDateButton}
        onClick={() => {
          // Clear both date and offer price
          setFormData(prev => ({
            ...prev,
            validTill: '',
            offeredPrice: ''
          }));
        }}
        disabled={loading}
      >
        <i className="pi pi-times"></i>
      </button>
    </div>
  </div>
)}

{/* Calendar appears below the offer price input when clicking the date */}
{formData.offeredPrice && !formData.validTill && showCalendar && (
  <div className={styles.calendarContainer} ref={calendarRef}>
    <CustomDatePicker
      value={formData.validTill}
      onChange={handleDateChange}
      minDate={getTomorrowDate()}
      placeholder="Select offer validity date"
      disabled={loading}
      width="100%"
    />
  </div>
)}
            </div>
          </div>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="numberOfPriests">Number of Priests</label>
          <input
            type="number"
            id="numberOfPriests"
            name="numberOfPriests"
            value={formData.numberOfPriests}
            onChange={(e) => handleInputChange('numberOfPriests', e.target.value)}
            placeholder="Number of priests"
            disabled={loading}
            min="1"
          />
        </div>

        {/* Recommended Events Dropdown - Single Input */}
        <div className={styles.formGroup} ref={eventsDropdownRef}>
          <label htmlFor="recommendedEvents">Recommended Events (Max 4)</label>
          
          {/* Selected events display as tags */}
          {/* Selected events display as tags */}
{formData.recommendedEventNames.length > 0 && (
  <div className={styles.selectedEventsTags}>
    {formData.recommendedEventNames.map((name, index) => {
      const eventId = formData.recommendedEvents[index];
      return (
        <div key={eventId} className={styles.eventTag}>
          <span className={styles.eventTagName}>{name}</span>
          <button 
            type="button"
            className={styles.eventTagRemove}
            onClick={() => removeSelectedEvent(eventId, name)}
            disabled={loading}
          >
            <i className="pi pi-times"></i>
          </button>
        </div>
      );
    })}
  </div>
)}
          
          {/* Single Input for events */}
          <div className={styles.eventsInputContainer}>
            <input
              type="text"
              id="recommendedEvents"
              value={getEventsDisplayValue()}
              onChange={handleEventsInputChange}
              onClick={handleEventsInputClick}
              placeholder={"Search and select events (max 4)"}
              disabled={loading}
              autoComplete="off"
            />
            <button 
              type="button"
              className={styles.eventsDropdownButton}
              onClick={() => setShowEventsDropdown(!showEventsDropdown)}
              disabled={loading}
            >
              <i className={`pi pi-chevron-${showEventsDropdown ? 'up' : 'down'}`}></i>
            </button>
            
            {showEventsDropdown && (
              <div className={styles.eventsDropdown}>
                <div className={styles.dropdownHeader}>
                  <span className={styles.dropdownTitle}>Select Events</span>
                  <span className={styles.selectedCount}>
                      {formData.recommendedEvents.length}/4 selected
                  </span>
                </div>
                
                <div className={styles.eventsList}>
                  {filteredEvents.length > 0 ? (
                    filteredEvents.map(event => {
                      const isSelected = formData.recommendedEvents.includes(event.id.toString());
                      return (
                        <div 
                          key={event.id} 
                          className={`${styles.eventOption} ${isSelected ? styles.eventOptionSelected : ''}`}
                          onClick={() => handleEventSelect(event)}
                        >
                          <span className={styles.eventOptionName}>{event.name}</span>
                          {isSelected && (
                            <span className={styles.eventOptionCheck}>
                              <i className="pi pi-check"></i>
                            </span>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className={styles.noEventsFound}>
                      <i className="pi pi-search"></i>
                      <span>No events found matching "{eventsSearch}"</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        
        {error && (
          <div className={styles.errorMessage}>
            <i className="pi pi-exclamation-triangle"></i> {error}
          </div>
        )}
        
        <div className={styles.formActions}>
          <button 
            type="button" 
            className={styles.cancelButton}
            onClick={handleCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className={styles.saveButton}
            disabled={loading}
          >
            {loading ? (
              <>
                <i className="pi pi-spin pi-spinner"></i> {mode === 'create' ? 'Creating...' : 'Updating...'}
              </>
            ) : (
              mode === 'create' ? 'Create Package' : 'Update Package'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateEditPriestPackageModal;